import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import LocationFilter from "../components/grants/LocationFilter";
import ArticlesList from "../components/grants/ArticlesList";
import ModuleMovedBanner from "../components/ModuleMovedBanner";
import { getGrantsArticles } from "../services/grantsApi";
import {
  KFS_ARTICLES_HREF,
  KFS_SNAPSHOT_LABEL,
} from "../constants/legacyGrantsRoutes";

interface LocationFilters {
  province?: string;
}

const GrantsArticles: React.FC = () => {
  const [filters, setFilters] = useState<LocationFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const { data, isLoading, error } = useQuery({
    queryKey: ["grants-articles", filters, currentPage, limit],
    queryFn: async () => {
      return await getGrantsArticles({
        province: filters.province,
        page: currentPage,
        limit: limit,
      });
    },
  });

  const handleLocationChange = (newFilters: LocationFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleProvinceClick = (province: string) => {
    setFilters({ province });
    setCurrentPage(1); // Reset to first page when province changes
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when limit changes
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Artykuły KFS — Urzędy Pracy
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Dane historyczne — ostatnia aktualizacja maj 2026
          </p>
        </div>

        <ModuleMovedBanner
          description="Ta strona zawiera dane historyczne — nie jest już aktualizowana. Wszystkie aktualności i nabory KFS zebrane w jednym widoku znajdziesz w nowym module Naborów."
          ctaLabel="Przejdź do aktualnych naborów KFS"
          ctaHref={KFS_ARTICLES_HREF}
          dataSnapshotLabel={KFS_SNAPSHOT_LABEL}
        />

        <LocationFilter
          onLocationChange={handleLocationChange}
          selectedProvince={filters.province}
          provinces={data?.provinces || []}
        />

        <div data-testid="grants-table-container" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie artykułów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              <p>Błąd podczas pobierania artykułów</p>
            </div>
          ) : (
            <ArticlesList
              articles={data?.articles || []}
              total={data?.total || 0}
              onProvinceClick={handleProvinceClick}
              pagination={data?.pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GrantsArticles;
